import { Center, Text, VStack } from '@chakra-ui/react';
import { Form, Formik } from 'formik';

import { useLEAccordionItemContext } from '../../../components/LEAccordion/LEAccordionItem/useLEAccordionItem';
import Button from '../../../components/ui/buttons/Button';
import PrimaryButton from '../../../components/ui/buttons/PrimaryButton';
import {
  UNSUPPORTED_LIENHOLDER_SELL_MSG,
  UNSUPPORTED_LIENHOLDER_SELL_SELECT_KEEP_MSG,
} from '../../../constants/tempInfos';
import { useTemporaryInfoUpdateMutation } from '../../../gql/generated/graphql';
import { useStep, useTempInfo } from '../../../hooks';
import useIsSellBlockedLienholder from '../../../hooks/useIsSellBlockedLienholder';
import useOptOutLookup from '../../../hooks/useOptOutLookup';
import { usePostLienholderRouting } from '../../../hooks/usePostLienholderRouting';
import { trackLead } from '../../../services/analytics/conversionEvents';
import { getInitialWantsToSell } from '../../flowEntryPages/utils';
import KeepMyCarSelector from './KeepMyCarSelector';

export type KeepMyCarFormValues = {
  wants_to_sell: string;
};

type KeepMyCarPanelProps = {
  unsupportedSellError: string | null;
  setUnsupportedSellError: (error: string | null) => void;
  sellOnlyPanel: boolean;
  onBuyout: () => Promise<void>;
  buyoutLoading: boolean;
};

// Standalone keep/sell question for authenticated users. The full PersonalInfo
// panel is hidden once logged in (they already have an account), but we still
// need their keep-or-sell decision, so this lightweight panel collects just that
// and resolves the same post-lienholder routing PersonalInfo would.
const KeepMyCarPanel = ({
  unsupportedSellError,
  setUnsupportedSellError,
  sellOnlyPanel,
  onBuyout,
  buyoutLoading,
}: KeepMyCarPanelProps) => {
  const step = useStep();
  const { updateState, isExpanded } = useLEAccordionItemContext();
  const { info } = useTempInfo({ fetchPolicy: 'network-only', refresh: isExpanded });
  const tempInfoData = info?.data;
  const [updateTemporaryInfo] = useTemporaryInfoUpdateMutation();
  const checkOptOut = useOptOutLookup();
  const resolveRouting = usePostLienholderRouting();
  const isSellBlockedLienholder = useIsSellBlockedLienholder();

  const onSubmit = async (values: KeepMyCarFormValues) => {
    const wantsToSell = sellOnlyPanel || values.wants_to_sell === 'true';

    const { data: updatedInfo } = await updateTemporaryInfo({
      variables: { info: { id: info?.id, data: { wants_to_sell: wantsToSell } } },
    });
    const updatedTempInfoData = updatedInfo?.temporaryInfoUpdate?.data ?? tempInfoData ?? null;

    if (wantsToSell && isSellBlockedLienholder(updatedTempInfoData)) {
      await updateTemporaryInfo({
        variables: { info: { id: info?.id, data: { wants_to_sell: false } } },
      });
      setUnsupportedSellError(
        sellOnlyPanel
          ? UNSUPPORTED_LIENHOLDER_SELL_MSG
          : UNSUPPORTED_LIENHOLDER_SELL_SELECT_KEEP_MSG,
      );
      return;
    }
    setUnsupportedSellError(null);

    await checkOptOut({ email: updatedTempInfoData?.email });
    trackLead({ tempInfoId: info?.id, email: updatedTempInfoData?.email ?? undefined });
    updateState({ status: 'complete' });

    const { skipPayoffAccordion } = await resolveRouting({
      wantsToSell,
      tempInfoData: updatedTempInfoData,
    });

    step.moveNext({ skipPayoffAccordion, wantsToSell });
  };

  return (
    <Formik<KeepMyCarFormValues>
      enableReinitialize
      initialValues={{
        wants_to_sell: getInitialWantsToSell(tempInfoData),
      }}
      onSubmit={onSubmit}
    >
      {({ isSubmitting, values, setFieldValue }) => (
        <Form>
          <VStack textAlign="center">
            {sellOnlyPanel ? (
              <Text>We'll email you as buyers make offers on your car.</Text>
            ) : (
              <KeepMyCarSelector values={values} setFieldValue={setFieldValue} />
            )}
          </VStack>
          <Center mt="30px">
            <PrimaryButton loading={isSubmitting} isDisabled={buyoutLoading} type="submit">
              NEXT: YOUR LEASE!
            </PrimaryButton>
          </Center>
          {unsupportedSellError && (
            <Text color="leaseEndRed" fontWeight="bold" mt={4} textAlign="center">
              {unsupportedSellError}
            </Text>
          )}
          {sellOnlyPanel && (
            <Text mt={4} textAlign="center">
              Want to keep your car?{' '}
              <Button variant="link" isLoading={buyoutLoading} onClick={onBuyout}>
                Buy it out instead
              </Button>
            </Text>
          )}
        </Form>
      )}
    </Formik>
  );
};

export default KeepMyCarPanel;
